import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useAppSettings, useUpdateAppSetting } from '@/hooks/useAppSettings';

export interface HighlightBarStyle {
  fill_color: string;
  fill_opacity: number;
  border_enabled: boolean;
  border_color: string;
  border_width: number;
}

export const HIGHLIGHT_BAR_STYLE_KEY = 'highlight_bar_style';

export const HIGHLIGHT_BAR_STYLE_DEFAULTS: HighlightBarStyle = {
  fill_color: '#ef4444',
  fill_opacity: 0.3,
  border_enabled: true,
  border_color: '#ef4444',
  border_width: 2,
};

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

const hexToRgba = (hex: string, opacity: number) => {
  if (!HEX_RE.test(hex)) return `rgba(239, 68, 68, ${opacity})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export function HighlightBarStyleEditor() {
  const { toast } = useToast();
  const { getSetting, isLoading } = useAppSettings();
  const updateMut = useUpdateAppSetting();

  const [form, setForm] = useState<HighlightBarStyle>(HIGHLIGHT_BAR_STYLE_DEFAULTS);

  useEffect(() => {
    if (isLoading) return;
    const saved = getSetting<HighlightBarStyle>(HIGHLIGHT_BAR_STYLE_KEY, HIGHLIGHT_BAR_STYLE_DEFAULTS);
    setForm({ ...HIGHLIGHT_BAR_STYLE_DEFAULTS, ...(saved || {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const update = <K extends keyof HighlightBarStyle>(field: K, value: HighlightBarStyle[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    if (!HEX_RE.test(form.fill_color)) {
      toast({ title: 'Lỗi', description: 'Fill color phải có định dạng #RRGGBB', variant: 'destructive' });
      return;
    }
    if (!HEX_RE.test(form.border_color)) {
      toast({ title: 'Lỗi', description: 'Border color phải có định dạng #RRGGBB', variant: 'destructive' });
      return;
    }
    const opacity = Number(form.fill_opacity);
    if (Number.isNaN(opacity) || opacity < 0 || opacity > 1) {
      toast({ title: 'Lỗi', description: 'Opacity phải nằm trong [0, 1]', variant: 'destructive' });
      return;
    }
    const width = Number(form.border_width);
    if (!Number.isFinite(width) || width < 0) {
      toast({ title: 'Lỗi', description: 'Border width phải >= 0', variant: 'destructive' });
      return;
    }

    updateMut.mutate(
      { key: HIGHLIGHT_BAR_STYLE_KEY, value: { ...form, fill_opacity: opacity, border_width: width } },
      {
        onSuccess: () =>
          toast({ title: 'Đã lưu', description: 'Cấu hình highlight bar đã được cập nhật.' }),
        onError: (err: Error) =>
          toast({ title: 'Lỗi', description: err.message || 'Không thể lưu cấu hình.', variant: 'destructive' }),
      }
    );
  };

  const previewBg = hexToRgba(form.fill_color, form.fill_opacity);
  const previewBorder = form.border_enabled
    ? `${form.border_width}px dashed ${form.border_color}`
    : 'none';

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Cấu hình Highlight bar</CardTitle>
          <p className="text-sm text-muted-foreground">
            Style áp cho các bar được highlight trong tab Plan và export. Chỉ admin có quyền lưu.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Fill color */}
          <div className="space-y-2">
            <Label htmlFor="fill_color">Fill color</Label>
            <div className="flex items-center gap-2">
              <Input
                id="fill_color"
                type="color"
                value={form.fill_color}
                onChange={(e) => update('fill_color', e.target.value)}
                className="w-12 h-10 p-1 border rounded"
              />
              <Input
                value={form.fill_color}
                onChange={(e) => update('fill_color', e.target.value)}
                placeholder="#ef4444"
                className="flex-1"
              />
            </div>
          </div>

          {/* Fill opacity */}
          <div className="space-y-2">
            <Label htmlFor="fill_opacity">Fill opacity (0 – 1)</Label>
            <Input
              id="fill_opacity"
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={form.fill_opacity}
              onChange={(e) => update('fill_opacity', parseFloat(e.target.value))}
            />
          </div>

          {/* Border enabled */}
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="border_enabled">Hiện border</Label>
              <p className="text-xs text-muted-foreground">Tắt để ẩn viền dashed.</p>
            </div>
            <Switch
              id="border_enabled"
              checked={form.border_enabled}
              onCheckedChange={(v) => update('border_enabled', v)}
            />
          </div>

          {/* Border color */}
          <div className="space-y-2">
            <Label htmlFor="border_color">Border color</Label>
            <div className="flex items-center gap-2">
              <Input
                id="border_color"
                type="color"
                value={form.border_color}
                onChange={(e) => update('border_color', e.target.value)}
                disabled={!form.border_enabled}
                className="w-12 h-10 p-1 border rounded"
              />
              <Input
                value={form.border_color}
                onChange={(e) => update('border_color', e.target.value)}
                disabled={!form.border_enabled}
                placeholder="#ef4444"
                className="flex-1"
              />
            </div>
          </div>

          {/* Border width */}
          <div className="space-y-2">
            <Label htmlFor="border_width">Border width (px)</Label>
            <Input
              id="border_width"
              type="number"
              min={0}
              step={1}
              value={form.border_width}
              onChange={(e) => update('border_width', parseInt(e.target.value, 10) || 0)}
              disabled={!form.border_enabled}
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={updateMut.isPending}>
              {updateMut.isPending ? 'Đang lưu...' : 'Lưu'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Live preview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Xem trước</CardTitle>
          <p className="text-sm text-muted-foreground">
            Bar mẫu render theo giá trị form hiện tại (chưa cần lưu).
          </p>
        </CardHeader>
        <CardContent>
          <div className="relative h-16 w-full rounded border bg-muted/30 overflow-hidden">
            <div
              className="absolute top-1/2 -translate-y-1/2 rounded-sm"
              style={{
                left: '15%',
                width: '60%',
                height: '12px',
                background: previewBg,
                outline: previewBorder,
                outlineOffset: '2px',
              }}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default HighlightBarStyleEditor;
